import {
  formatMoney,
  type AdminComplimentaryGrant,
  type AdminEntitlement,
  type AdminHouseholdDetail,
  type AdminHouseholdMember,
  type AdminPendingInvite,
  type AdminSubscriptionRow,
  type HouseholdRole,
  type PlanId,
  type SubscriptionStatus,
  type SubscriptionStore,
} from '@platewell/schema';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { DataTable, type DataTableColumn } from '../components/data-table.js';
import { Dialog } from '../components/dialog.js';
import { AbsoluteDate, RelativeTime } from '../components/relative-time.js';
import { ApiError, getAdminHousehold, grantComplimentary } from '../lib/api.js';
import { adminHouseholdRoute } from '../router.js';

/**
 * One household, in full.
 *
 * The lists answer "which household is this?"; this page answers everything support is
 * asked next — who is in it, how much is in it, when they were last here, and what the
 * The one write it offers is the complimentary grant, because a household that should
 * never have been charged is the thing support cannot otherwise fix from here. Everything
 * else stays read-only.
 *
 * Fetched in the component rather than in a route loader, to match the two lists and for
 * the same reason they are: a loader runs before AdminShell has decided whether this
 * person may be here at all, so a non-admin would fire the request just to be refused.
 */
export function AdminHousehold() {
  const { householdId } = adminHouseholdRoute.useParams();
  // Carried through from the list so Back lands on the page and query it was opened from.
  const search = adminHouseholdRoute.useSearch();
  const [detail, setDetail] = useState<AdminHouseholdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  const grant = useCallback(
    async (reason: string) => {
      setGrantError(null);
      try {
        // The response is the whole household as the server now holds it, so nothing here
        // has to model what a comp does to the plan, the limit or the status.
        setDetail(await grantComplimentary(householdId, reason));
        setGranting(false);
      } catch (cause: unknown) {
        const code = cause instanceof ApiError ? cause.code : '';
        setGrantError(
          GRANT_REFUSALS[code] ??
            (cause instanceof Error ? cause.message : 'Could not grant the subscription.'),
        );
        setGranting(false);
      }
    },
    [householdId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getAdminHousehold(householdId)
      .then((value) => {
        if (cancelled) return;
        setDetail(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setError(
          cause instanceof ApiError && cause.status === 404
            ? 'No household with that id. It may have been purged.'
            : 'Could not load this household.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  return (
    <>
      <div className="admin-page-head">
        <Link className="admin-back" to="/admin/households" search={search}>
          ‹ Households
        </Link>
        <h1>
          {detail?.name ?? (loading ? 'Loading…' : 'Household')}
          {detail?.deleted === true && <span className="data-table-flag">deleted</span>}
        </h1>
        {/* The id is what a support ticket, a log line and the reproject endpoint all
            carry, so it is on the page rather than only in the address bar. */}
        <span className="admin-range">{householdId}</span>
      </div>

      {error != null && <p className="error-text">{error}</p>}

      {detail != null && (
        <>
          <dl className="admin-stats">
            <AdminStat label="Members" value={String(detail.members.length)} />
            <AdminStat
              label="Recipes"
              value={String(detail.recipeCount)}
              // The count is the projected read model's, not the CRDT's — a household
              // behind on sync reports fewer recipes than it actually holds.
              note={detail.deletedRecipeCount > 0 ? `${detail.deletedRecipeCount} in trash` : null}
            />
            <AdminStat label="Plan" value={PLAN_LABELS[detail.entitlement.plan]} />
            <AdminStat
              label="Created"
              value={<AbsoluteDate value={detail.createdAt} />}
              note={
                detail.deletedAt == null
                  ? null
                  : `Deleted ${new Date(detail.deletedAt).toLocaleDateString()}`
              }
            />
          </dl>

          <section className="admin-section">
            <h2 className="section-title">Members</h2>
            <DataTable
              caption="Household members"
              columns={MEMBER_COLUMNS}
              rows={detail.members}
              rowKey={(row) => row.id}
              // Every member is on the page: a household is small enough that paging it
              // would hide the one person the ticket is about.
              total={detail.members.length}
              page={1}
              pageSize={Math.max(1, detail.members.length)}
              sort=""
              dir="asc"
              loading={false}
              onSortChange={() => undefined}
              onPageChange={() => undefined}
              density="compact"
              rowClassName={(row) => (row.deleted ? 'data-table-row--deleted' : undefined)}
              emptyLabel="Nobody belongs to this household."
            />
          </section>

          {detail.pendingInvites.length > 0 && (
            <section className="admin-section">
              <h2 className="section-title">Pending invites</h2>
              <DataTable
                caption="Pending invites"
                columns={INVITE_COLUMNS}
                rows={detail.pendingInvites}
                rowKey={(row) => row.id}
                total={detail.pendingInvites.length}
                page={1}
                pageSize={Math.max(1, detail.pendingInvites.length)}
                sort=""
                dir="asc"
                loading={false}
                onSortChange={() => undefined}
                onPageChange={() => undefined}
                density="compact"
              />
            </section>
          )}

          <section className="admin-section">
            <h2 className="section-title">Subscription</h2>
            <EntitlementFacts entitlement={detail.entitlement} grant={detail.complimentaryGrant} />

            {!detail.entitlement.complimentary && (
              <div className="row">
                <button type="button" className="btn" onClick={() => setGranting(true)}>
                  Grant complimentary subscription
                </button>
              </div>
            )}
            {grantError != null && <p className="error-text">{grantError}</p>}

            {detail.subscriptions.length > 0 && (
              <DataTable
                caption="Subscription rows"
                columns={SUBSCRIPTION_COLUMNS}
                rows={detail.subscriptions}
                rowKey={(row) => row.id}
                total={detail.subscriptions.length}
                page={1}
                pageSize={Math.max(1, detail.subscriptions.length)}
                sort=""
                dir="asc"
                loading={false}
                onSortChange={() => undefined}
                onPageChange={() => undefined}
                density="compact"
              />
            )}
          </section>

          {granting && (
            <GrantComplimentaryDialog
              householdName={detail.name}
              // Comping does not reach the store, so an agent about to comp a household
              // that is still being charged needs telling before they click, not after.
              paidSubscription={detail.subscriptions.some(
                (row) => row.status === 'active' || row.status === 'trialing',
              )}
              onClose={() => setGranting(false)}
              onGrant={grant}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * The governing entitlement, which is not the same thing as any one subscription row.
 *
 * `household_entitlement` is what the app actually enforces, derived from every row the
 * household holds; the table below it is the raw history. Support needs both, and needs to
 * see when they disagree — a cancelled subscription beside a household still on `pro` is
 * the paid-period-not-yet-over case, not a bug.
 */
function EntitlementFacts({
  entitlement,
  grant,
}: {
  entitlement: AdminEntitlement;
  grant: AdminComplimentaryGrant | null;
}) {
  return (
    <div className="admin-facts">
      <div className="kv">
        <span>Plan</span>
        <span>
          {PLAN_LABELS[entitlement.plan]}
          {entitlement.complimentary && ' · complimentary'}
        </span>
      </div>
      <div className="kv">
        <span>Recipe limit</span>
        <span className="data-table-num">{entitlement.recipeLimit ?? 'Unlimited'}</span>
      </div>
      <div className="kv">
        <span>Status</span>
        <span>{STATUS_LABELS[entitlement.status]}</span>
      </div>
      <div className="kv">
        <span>{entitlement.cancelAtPeriodEnd ? 'Access until' : 'Renews'}</span>
        <span>
          {entitlement.currentPeriodEnd == null ? (
            <span className="hint">Not applicable</span>
          ) : (
            <AbsoluteDate value={entitlement.currentPeriodEnd} />
          )}
        </span>
      </div>
      {entitlement.pastDueSince != null && (
        // Only while payment is actually failing. The 3-day grace is measured from here,
        // so this is the timestamp that decides when the household locks.
        <div className="kv">
          <span>Payment failing since</span>
          <span>
            <RelativeTime value={entitlement.pastDueSince} />
          </span>
        </div>
      )}
      {entitlement.complimentaryReason != null && (
        <div className="kv">
          {/* Labelled as the customer's, because that is who reads it: it is printed on
              their own settings screen under "Included at no charge". */}
          <span>Shown to the customer</span>
          <span>{entitlement.complimentaryReason}</span>
        </div>
      )}
      {grant != null && entitlement.complimentary && (
        <div className="kv">
          <span>Comped by</span>
          <span>
            {grant.adminName ?? 'a deleted account'} · <RelativeTime value={grant.grantedAt} />
          </span>
        </div>
      )}
      <div className="kv">
        <span>Entitlement updated</span>
        <span>
          <RelativeTime value={entitlement.updatedAt === 0 ? null : entitlement.updatedAt} />
        </span>
      </div>
    </div>
  );
}

function AdminStat({
  label,
  value,
  note = null,
}: {
  label: string;
  value: ReactNode;
  note?: string | null;
}) {
  return (
    <div className="admin-stat">
      <dt>{label}</dt>
      <dd>
        {value}
        {note != null && <span className="admin-stat-note">{note}</span>}
      </dd>
    </div>
  );
}

/**
 * Why a grant was refused, in the agent's own words.
 *
 * `already_complimentary` is the one worth saying plainly: two agents on the same ticket
 * is the ordinary way to reach it, and the household ends up comped either way.
 */
const GRANT_REFUSALS: Record<string, string> = {
  already_complimentary: 'This household is already comped. Reload to see the current grant.',
  not_found: 'That household no longer exists.',
  invalid_request: 'A reason is required, and must be under 300 characters.',
  forbidden: 'Your session cannot grant this. Sign in again as yourself.',
};

/**
 * Asks for the reason, and says plainly what the grant does before it is made.
 *
 * Two things are worth stating in front of an agent who is about to click: the reason is
 * shown to the customer, and the grant does not reach the store — a household that is
 * being charged carries on being charged.
 */
function GrantComplimentaryDialog({
  householdName,
  paidSubscription,
  onClose,
  onGrant,
}: {
  householdName: string;
  paidSubscription: boolean;
  onClose: () => void;
  onGrant: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const trimmed = reason.trim();
  const canSubmit = trimmed !== '' && trimmed.length <= 300 && !busy;

  function submit() {
    if (!canSubmit) return;
    setBusy(true);
    // The caller owns the error and closes the dialog either way, so there is nothing to
    // catch here beyond releasing the button if it somehow stays open.
    void onGrant(trimmed).finally(() => setBusy(false));
  }

  return (
    <Dialog
      onClose={busy ? () => undefined : onClose}
      aria-label="Grant complimentary subscription"
    >
      <h2>Grant complimentary subscription</h2>
      <p className="hint">
        {householdName} gets all their recipes, for life, with no card on file and nothing to
        renew.
      </p>
      {paidSubscription && (
        <p className="error-text">
          This household is still being charged. Comping it here does not cancel anything at
          the App Store, Google Play or Stripe — that has to be done there.
        </p>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="field">
          <span className="label">Reason</span>
          <input
            className="input"
            value={reason}
            autoFocus
            maxLength={300}
            placeholder="Beta tester since launch"
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <p className="hint">The customer sees this on their own settings screen.</p>
        <div className="row" style={{ marginTop: 18 }}>
          <span className="spacer" />
          <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            {busy ? 'Granting…' : 'Grant'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

const MEMBER_COLUMNS: DataTableColumn<AdminHouseholdMember>[] = [
  {
    key: 'name',
    label: 'Person',
    sortable: false,
    render: (row) => (
      <>
        <span className="data-table-strong">{row.name}</span>
        {row.deleted && <span className="data-table-flag">deleted</span>}
        {row.admin && <span className="data-table-flag data-table-flag--note">admin</span>}
        <span className="data-table-subline">{row.email}</span>
      </>
    ),
  },
  {
    key: 'role',
    label: 'Role',
    sortable: false,
    render: (row) => ROLE_LABELS[row.role],
  },
  {
    key: 'joinedAt',
    label: 'Joined',
    sortable: false,
    render: (row) => <AbsoluteDate value={row.joinedAt} />,
  },
  {
    key: 'lastLoggedInAt',
    label: 'Last logged in',
    sortable: false,
    render: (row) => <RelativeTime value={row.lastLoggedInAt} />,
  },
  {
    key: 'loginCount',
    label: 'Logins',
    align: 'right',
    sortable: false,
    // The account's total across every household it belongs to; sign-ins are not counted
    // per household, and a member of three books shows the same number on all three.
    render: (row) => <span className="data-table-num">{row.loginCount}</span>,
  },
];

const INVITE_COLUMNS: DataTableColumn<AdminPendingInvite>[] = [
  {
    key: 'email',
    label: 'Invited',
    sortable: false,
    render: (row) => <span className="data-table-strong">{row.email}</span>,
  },
  {
    key: 'role',
    label: 'Role',
    sortable: false,
    render: (row) => ROLE_LABELS[row.role],
  },
  {
    key: 'createdAt',
    label: 'Sent',
    sortable: false,
    render: (row) => <RelativeTime value={row.createdAt} />,
  },
  {
    key: 'expiresAt',
    label: 'Expires',
    sortable: false,
    render: (row) => <AbsoluteDate value={row.expiresAt} />,
  },
];

const SUBSCRIPTION_COLUMNS: DataTableColumn<AdminSubscriptionRow>[] = [
  {
    key: 'store',
    label: 'Bought through',
    sortable: false,
    render: (row) => (
      <>
        <span className="data-table-strong">{STORE_LABELS[row.store]}</span>
        {row.productId != null && <span className="data-table-subline">{row.productId}</span>}
      </>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortable: false,
    render: (row) => (
      <>
        {STATUS_LABELS[row.status]}
        {row.cancelAtPeriodEnd && (
          <span className="data-table-subline">will not renew</span>
        )}
      </>
    ),
  },
  {
    key: 'price',
    label: 'Price',
    align: 'right',
    sortable: false,
    // What this household was actually charged, in the currency it was charged in — the
    // USD list price is not what a household that bought in euros is paying.
    render: (row) =>
      row.currency == null || row.amountMinor == null ? (
        <span className="hint">—</span>
      ) : (
        <span className="data-table-num">{formatMoney(row.amountMinor, row.currency)}</span>
      ),
  },
  {
    key: 'currentPeriodEnd',
    label: 'Period ends',
    sortable: false,
    render: (row) => <AbsoluteDate value={row.currentPeriodEnd} never="—" />,
  },
  {
    key: 'startedByName',
    label: 'Bought by',
    wrap: true,
    sortable: false,
    render: (row) =>
      row.startedByName ?? <span className="hint">Unknown</span>,
  },
];

const PLAN_LABELS: Record<PlanId, string> = {
  free: 'Free',
  pro: 'Pro',
  locked: 'Locked',
};

const ROLE_LABELS: Record<HouseholdRole, string> = {
  owner: 'Owner',
  editor: 'Editor',
  viewer: 'Viewer',
};

/** Sentence case, and `past_due` said in words rather than in the column's own spelling. */
const STATUS_LABELS: Record<'none' | SubscriptionStatus, string> = {
  none: 'Never subscribed',
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Payment failing',
  paused: 'Paused',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const STORE_LABELS: Record<SubscriptionStore, string> = {
  app_store: 'App Store',
  play_store: 'Google Play',
  complimentary: 'Platewell',
  web: 'Web (Stripe)',
};
